import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersGetMeOptions } from '@givmo/api-client/query';
import { BOARD_SLUG, BoardOfficerProvider, BoardOfficerToDo } from '@givmo/board';
import { ModuleGate } from '@givmo/org-console';
import { NotFound } from '../../components/not-found';
import { useRequestAccessDoor } from '../../components/request-access-context';
import { useCharityConsole } from '../../console/charity-console-context';
import { TEAM_ACCESS_ROUTE } from '../../console/console-routes';
import { useModuleNavigate } from '../../console/use-console-squares';
import { useTurnOnHref } from '../../console/use-turn-on-href';
import { useActiveOrg } from '../../org/active-org-context';

/** The console page listing what is connected to the organization, as one stable object. */
const CONNECTED_AI = { href: TEAM_ACCESS_ROUTE } as const;

/** Officer-only: directors enter through the separate root routes and their own session. */
export function CharityBoardMount({ children }: { children: ReactNode }) {
  const { pending, errored, standings, read, refetch } = useCharityConsole();
  const org = useActiveOrg();
  const door = useRequestAccessDoor();
  const navigate = useModuleNavigate();
  const turnOnHref = useTurnOnHref(BOARD_SLUG);
  // The caller's BACKEND user id (never the Firebase uid), the same read the org console's config
  // makes: the calendar's own-calendar and calendar-file rules compare connection owners to it.
  const me = useQuery(usersGetMeOptions());
  const standing = standings?.board;
  const request = standing?.accessRequest;
  return (
    <ModuleGate
      slug={BOARD_SLUG}
      label="Board"
      read={{ pending, errored, standings }}
      onRetry={refetch}
      renderNotFound={() => <NotFound />}
      turnOnHref={turnOnHref}
      onNavigate={navigate}
      onRequestAccess={standing?.held === true ? undefined : () => door.open(BOARD_SLUG)}
      accessRequest={
        request?.status === 'PENDING'
          ? { status: 'pending' }
          : request?.status === 'DENIED'
            ? { status: 'declined', note: request.resolutionNote ?? null }
            : undefined
      }
    >
      <BoardOfficerProvider
        key={org.activeCharityId}
        host={{
          orgRef: org.isReady ? org.activeCharityId : null,
          isReady: org.isReady && !pending && !errored,
          isPending: org.isPending || pending,
          isError: org.isError || errored,
          error: org.error,
          permissions: read.permissions,
          orgTimeZone: read.timeZone,
          userId: me.data?.id ?? null,
          userEmail: me.data?.email ?? null,
          orgName: org.activeMembership?.name ?? null,
          // The organization's connected AI tools (cut inbox's review fold, M2), the page the CRM
          // import's "How to connect" opens.
          connectedAi: CONNECTED_AI,
        }}
      >
        {children}
        {/* CBG-UX7-WEB-OFF: the officer's to-do list, beside every Board page and nowhere else. */}
        <BoardOfficerToDo />
      </BoardOfficerProvider>
    </ModuleGate>
  );
}
